import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @file Modal 弹窗组件
 */
import { omit } from '@seiue/util';
import ModalAntd from 'antd/es/modal';
import React, { useRef, useImperativeHandle, useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { ButtonSwift } from './ButtonSwift';
import { ModalFooter } from './Footer';
import { ModalHeader } from './Header';
import { useFullModalHeight } from './use-full-modal-height';
export * from './types';
export const Modal = React.forwardRef((props, ref) => {
    const { visible, closable = true, scrollable = true, height, maxHeight, fullScreen = false, showRightSwift = false, showLeftSwift = false, rightSwiftText, leftSwiftText, onSwift, width, } = props;
    let modalHeight = typeof height === 'number' ? `${height}px` : height;
    if (!modalHeight && !scrollable) {
        // 当设置为不可滚动，且无设置 modalHeight 时，给予一个默认高度
        modalHeight = '80vh';
    }
    const fullModalHeight = useFullModalHeight(fullScreen);
    if (fullScreen) {
        modalHeight = fullModalHeight;
    }
    const bodyRef = useRef(null);
    const [modalWrapDom, setModalWrapDom] = useState(null);
    const [modalBodyWidth, setModalBodyWidth] = useState(0);
    useEffect(
    // Modal 消失时，置 Ref 为空
    () => () => {
        if (bodyRef.current)
            bodyRef.current = null;
    }, []);
    useImperativeHandle(ref, () => ({
        getBodyDom: () => bodyRef.current,
    }));
    const antdModalProps = omit(props, [
        'header',
        'footer',
        'closable',
        'title',
        'headerRight',
        'cancelButtonProps',
        'onOk',
        'okType',
        'okButtonProps',
        'children',
        'visible',
    ]);
    return (_jsxs(ModalAntdStyled, Object.assign({ maskClosable: false, keyboard: false, centered: true }, antdModalProps, { open: visible, title: "", footer: null, closable: false, fullScreen: fullScreen, width: width, bodyStyle: {
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: maxHeight || (fullScreen ? '100vh' : '80vh'),
            minHeight: '140px',
            height: modalHeight,
        }, destroyOnClose: true }, { children: [_jsx(ModalHeader, { header: props.header, title: props.title, style: props.headerStyle || {}, headerRight: props.headerRight, closable: closable, onCancel: props.onCancel, onClose: props.onClose }, void 0), _jsx(ModalBody, Object.assign({ ref: v => {
                    if (v) {
                        bodyRef.current = v;
                        // 等待弹窗本身的动画效果
                        setTimeout(() => {
                            if (bodyRef.current) {
                                setModalBodyWidth(v.offsetWidth);
                                setModalWrapDom(v.closest('.ant-modal-wrap'));
                            }
                        }, 300);
                    }
                }, style: props.bodyStyle, scrollable: scrollable, fullScreen: fullScreen }, { children: props.children }), void 0), _jsx(ModalFooter, Object.assign({}, props), void 0), showLeftSwift ? (_jsx(LeftButtonSwift, { modalWidth: modalBodyWidth, targetDom: modalWrapDom, direction: "left", onSwift: onSwift, label: leftSwiftText }, "left")) : null, showRightSwift ? (_jsx(RightButtonSwift, { modalWidth: modalBodyWidth, targetDom: modalWrapDom, direction: "right", onSwift: onSwift, label: rightSwiftText }, "right")) : null] }), void 0));
});
const ModalAntdStyled = styled(ModalAntd) `
  overflow: hidden;

  &&& {
    ${props => props.fullScreen
    ? css `
            /* 父元素是 position: fixed; 所以设置成 100%（不是 100vw），避免某些情况下出现竖直滚动条时因为宽度不够掉到下面而导致的 bug */
            width: 100% !important;
            max-width: 100%;
            height: 100% !important;
            padding: 0 !important;
            margin: 0;
            overflow: hidden;
            .ant-modal-content {
              border-radius: 0;
              .ant-modal-body > div {
                border-radius: 0;
              }
            }
          `
    : css `
            width: ${props.width};
            max-height: 80vh;
            top: -5vh !important;
            .ant-modal-content {
              border-radius: 6px;
            }
          `}
  }
`;
export const ModalBody = styled.div `
  flex: 1;
  height: ${({ scrollable }) => (scrollable ? 'auto' : '100%')};
  padding: ${({ fullScreen }) => (fullScreen ? '0px' : '24px')};
  overflow: ${({ scrollable }) => (scrollable ? 'auto' : 'hidden')};
`;
const StyledButtonSwift = styled(ButtonSwift) `
  position: absolute;
  top: 50%;
  z-index: 1;
  height: 48px;
  padding: 4px 16px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 18px;
  background-color: rgba(6, 12, 25, 0.5);
  border: none;
  border-radius: 24px;

  & + & {
    margin: 0;
  }

  &:hover,
  &:focus {
    color: rgba(255, 255, 255, 0.8);
    background-color: rgba(6, 12, 25, 0.6);
  }

  &:active {
    color: ${p => p.theme.text._0};
    background-color: rgba(6, 12, 25, 0.6);
  }
`;
const LeftButtonSwift = styled(StyledButtonSwift) `
  left: calc(50vw - ${props => props.modalWidth / 2}px - 48px);
  transform: translate(-100%, -50%);
`;
const RightButtonSwift = styled(StyledButtonSwift) `
  right: calc(50vw - ${props => props.modalWidth / 2}px - 48px);
  transform: translate(100%, -50%);
`;
//# sourceMappingURL=data:application/json;base64,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